import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GisDataModel, GisFieldsEnum } from '@bo-client/shared/models/gisData';
import { FormGroup, Validators } from '@angular/forms';
import { DevelopmentAreaInfo } from '@bo-client/shared/models/developmentAreas';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { GisAddressService } from '@bo-client/shared/components/get-gis-data/gis-address.service';
import { Observe } from '@bo-client/shared/observe.util';

@Component({
  selector: 'app-get-gis-data',
  templateUrl: './get-gis-data.component.html',
  styleUrls: ['./get-gis-data.component.scss']
})
export class GetGisDataComponent implements OnInit, OnDestroy {
  @Input() askForDevelopmentArea = true;
  @Input() areaType: AreaTypeEnum;
  @Input() areaTypes: AreaTypeEnum[];
  @Input() gisDataForm: FormGroup;
  @Input() disable = false;
  @Input() eventsFromParent: Observable<string>;
  @Input() listNewDevelopmentAreas: DevelopmentAreaInfo[];
  @Input() isNewDevelopmentArea: boolean;
  @Input() selectedGisData: GisDataModel;
  @Output() gisData: EventEmitter<GisDataEvent> = new EventEmitter();
  @Output() resetCompanyLocation: EventEmitter<any> = new EventEmitter();

  @Observe('gisDataForm') gisDataForm$: Observable<FormGroup>;

  private eventsSubscription: Subscription = new Subscription();

  public isNotFound = false;
  public areaAvailable = true;
  private compUnsubs$ = new Subject<void>();
  private unsubsChangeValues$ = new Subject<void>();

  public developmentAreasByZipCode: DevelopmentAreaInfo[];
  _developmentAreaStreetName$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  developmentAreaStreetName$: Observable<string> = this._developmentAreaStreetName$.asObservable();

  public cityOptions: GisDataModel[] = null;
  public streetOptions: GisDataModel[] = null;
  public streetNoOptions: GisDataModel[] = null;
  public disableCorridorCheck = true;
  public isCompleted = false;
  public isSearching = false;

  public isNoHouseNumber = false;

  public fakeIdStreetNumberNotFound = '99999ABC';
  public gisFieldsEnum = GisFieldsEnum;

  get streetNoSelected(): boolean {
    return this.gisDataForm.get('uniqueCoId').value;
  }

  public showSelectMessage = false;

  constructor(
    private gisAddressService: GisAddressService, // TODO cleanup => This makes no sense
    private getGisDataInterfaceService: GisAddressService //TODO: das geht glaube ich so nicht. Bei Constructor ist ja noch nicht entshcieden welcher es ist. So wirst du einen Mandantenwechsel nicht aushalten
  ) {}

  ngOnInit(): void {
    this.eventsSubscription = this.eventsFromParent?.pipe(takeUntil(this.compUnsubs$)).subscribe((x) => {
      if (x == 'setDisable') {
        this.compUnsubs$.next();
      }
    });
    this.gisDataForm$.pipe(takeUntil(this.compUnsubs$)).subscribe({
      next: () => {
        this.initFields();
        this.searchCityZipCode();
        this.searchStreet();
        this.searchStreetNo();
        this.searchNewDevelopmentArea();
        this.observeCorridorNumberChanges();
      }
    });
  }

  closedStreetNoSelect() {
    this.showSelectMessage = !this.streetNoSelected;
    if (!this.streetNoSelected) {
      console.log('resetting streetno....');
      this.gisDataForm.get('streetNo').setValidators([Validators.required]);
      this.gisDataForm.get('streetNo').patchValue('');
      this.gisDataForm.get('streetNoAddition').patchValue('');
      this.gisDataForm.get('uniqueCoId').patchValue('');
      this.gisDataForm.get('streetNo').markAsTouched();
    }
  }

  searchNewDevelopmentArea() {
    this.newDevelopmentAreaField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null && val != ''),
        filter((val) => val.toString().length > 0),
        tap(() => (this.isSearching = true)),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getCitySuggestions(val)),
        tap(() => (this.isSearching = false))
      )
      .subscribe();
  }

  searchCityZipCode() {
    this.zipCodeCityField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null && val != ''),
        filter((val) => val.toString().length > 0),
        tap(() => (this.isSearching = true)),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getCitySuggestions(val)),
        tap(() => (this.isSearching = false)),
        map((x) => (this.cityOptions = x))
      )
      .subscribe();
  }

  searchStreet() {
    this.streetField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null),
        filter((val) => val.toString().length > 0),
        tap(() => (this.isSearching = true)),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getStreetSuggestions(this.selectedGisData, val)),
        tap(() => (this.isSearching = false)),
        map((x) => (this.streetOptions = x))
      )
      .subscribe();
  }

  searchStreetNo() {
    this.isNotFound = false;
    this.streetNoField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null),
        filter((val) => val.toString().length > 0),
        tap(() => {
          this.isNotFound = false;
          this.isSearching = true;
          this.showSelectMessage = false;
        }),
        distinctUntilChanged(),
        switchMap((val) => this.getGisDataInterfaceService.getStreetNoSuggestions(this.selectedGisData, val)),
        tap(() => (this.isSearching = false)),
        map((res) => {
          // Todo: this is not so nice :)
          this.streetNoOptions = res;
          if (this.streetNoOptions.length == 0) {
            this.isNotFound = true;
            this.streetNoOptions.push({
              id: this.fakeIdStreetNumberNotFound,
              houseNo: this.gisDataForm.get('streetNo').value
            });
          }
        })
      )
      .subscribe();
  }

  observeCorridorNumberChanges() {
    this.isNotFound = false;
    this.corridorNumberField.valueChanges
      .pipe(
        takeUntil(this.compUnsubs$),
        debounceTime(400),
        filter((val) => val != null),
        filter((val) => val.toString().length > 0),
        distinctUntilChanged()
      )
      .subscribe((res) => {
        this.selectedGisData.corridorNumber = res;
      });
  }

  corridorNumberSelected() {
    if (this.selectedGisData.corridorNumber && this.selectedGisData.corridorNumber != '') {
      this.confirmCorridorNumber();
      this.emitGisData();
    }
  }

  initFields() {
    if (this.isNewDevelopmentArea) {
      this.gisDataForm.get('corridorNumber').setValidators([Validators.required]);
      this.gisDataForm.get('corridorNumber').markAsTouched();
    } else {
      this.gisDataForm.get('streetNo').setValidators([Validators.required]);
      this.gisDataForm.get('streetNo').markAsTouched();
    }
    this.gisDataForm.get('street').disable();
    this.gisDataForm.get('streetNo').disable();
    this.gisDataForm.get('developmentAreaName').disable();
    this.gisDataForm.get('corridorNumber').disable();
    this.disableCorridorCheck = true;
  }

  resetGisData() {
    this.selectedGisData = null;
    this.isCompleted = false;
    this.cityOptions = null;
    this.streetNoOptions = null;
    this.developmentAreasByZipCode = null;
    this.isNotFound = false;
    // unsubscribe form valueChanges
    this.unsubsChangeValues$.next();
    this.unsubsChangeValues$.complete();
    this.gisDataForm.reset();
    this.gisDataForm.get('zipCodeCity').patchValue('');
    this.gisDataForm.get('street').patchValue('');

    this.resetHouseNoFormState();

    this.gisDataForm.get('street').disable();
    this.gisDataForm.get('streetNo').disable();

    this.gisDataForm.get('developmentAreaName').patchValue('');
    this.gisDataForm.get('developmentAreaName').disable();

    this.gisDataForm.get('corridorNumber').patchValue('');
    this.gisDataForm.get('corridorNumber').disable();
    this.disableCorridorCheck = true;
    this.isNoHouseNumber = false;

    this.searchStreet();
    this.searchStreetNo();
    this.gisData.emit({
      gisData: this.selectedGisData,
      objectNotFound: this.isNotFound,
      areaAvailable: this.areaAvailable
    });
  }

  resetHouseNoFormState() {
    this.showSelectMessage = false;
    if (this.isNewDevelopmentArea || this.isNoHouseNumber) {
      this.gisDataForm.get('corridorNumber').setValidators([Validators.required]);
      this.gisDataForm.get('corridorNumber').patchValue('');
      this.gisDataForm.get('corridorNumber').markAsTouched();
      this.gisDataForm.get('corridorNumber').enable();
      this.gisDataForm.get('streetNo').clearValidators();
      this.gisDataForm.get('streetNo').patchValue(null);
      this.gisDataForm.get('streetNo').markAsTouched();
      this.observeCorridorNumberChanges();
    } else {
      this.gisDataForm.get('streetNo').setValidators([Validators.required]);
      this.gisDataForm.get('corridorNumber').setValidators([]);
      this.resetStreetNo();
      this.gisDataForm.get('streetNo').markAsTouched();
      this.gisDataForm.get('streetNo').enable();
      this.disableCorridorCheck = false;
    }
  }

  selectedOption(option: GisDataModel | DevelopmentAreaInfo, field: GisFieldsEnum) {
    this.resetChangeValues();
    if (!this.selectedGisData) {
      this.selectedGisData = new GisDataModel();
    }
    // load selectedGisDataFields
    this.selectedGisData = this.gisAddressService.loadSelectedGisData(field, option, this.selectedGisData);
    this._developmentAreaStreetName$.next(null);

    console.log('Selected option', option, this.selectedGisData, field);
    // Set Form fields
    switch (field) {
      case GisFieldsEnum.zipCode:
        // this.gisDataForm.
        option = option as GisDataModel;
        this.gisDataForm.get('street').patchValue('');
        this.gisDataForm.get('city').patchValue(option.city);
        this.gisDataForm.get('zipCode').patchValue(option.zipCode);
        this.resetStreetNo();
        this.streetOptions = null;
        this.streetNoOptions = null;
        this.gisDataForm.get('street').enable();
        this.gisDataForm.get('streetNo').disable();
        this.gisDataForm.get('developmentAreaName').enable();
        this.gisDataForm.get('corridorNumber').disable();
        this.setNullCompanyLocation();
        break;
      case GisFieldsEnum.street:
        option = option as GisDataModel;
        this.streetNoOptions = null;
        this.resetStreetNo();
        // Todo.. make this reactive..
        this.gisDataForm.get('streetNo').enable();
        this.disableCorridorCheck = false;
        this.setNullCompanyLocation();
        break;
      case GisFieldsEnum.streetNo:
        // tslint:disable-next-line:triple-equals
        option = option as GisDataModel;
        if (option.id != this.fakeIdStreetNumberNotFound) {
          this.areaAvailable = this.getGisDataInterfaceService.isAvailableArea(
            this.areaType,
            this.areaTypes,
            option
          );
          console.log('Available Area selected', this.areaAvailable);
        }
        // if streetNo not found => rewrite houseNo with the fieldvalue
        if (this.isNotFound) {
          this.selectedGisData.houseNo = this.gisDataForm.get('streetNo').value;
        }

        // TODO: hier die richtige Zählerpunkt-ID Speichern. Oder in Impl. des Adress-Services verschieben
        // Save Gis Unique identifier
        this.saveUniqueCoId();
        this.gisDataForm.get('uniqueCoId').patchValue(this.selectedGisData.uniqueCoId);
        this.gisDataForm.get('streetNoAddition').patchValue(this.selectedGisData.houseNoAddition);
        this.gisDataForm.get('streetNo').patchValue(this.selectedGisData.houseNo);
        this.emitGisData();
        break;
      case GisFieldsEnum.developmentArea:
        option = option as DevelopmentAreaInfo;
        this._developmentAreaStreetName$.next(option.defaultStreetName);
        // Set Form fields
        this.gisDataForm.get('corridorNumber').patchValue('');
        this.gisDataForm.get('corridorNumber').enable();
        break;
    }

    this.selectedGisData.isNewDevelopmentArea = this.isNewDevelopmentArea;

    if (this.selectedGisData.isNewDevelopmentArea) {
      this.developmentAreasByZipCode = this.listNewDevelopmentAreas.filter(
        (x) => x.zipCode == this.selectedGisData.zipCode
      );
    }
  }

  private resetConnectionObject() {
    this.resetGisData();
  }

  private resetStreet() {
    this.gisDataForm.get('street').patchValue('');
    this.gisDataForm.get('street').enable();
  }

  private resetStreetNo() {
    this.gisDataForm.get('streetNo').patchValue('');
    this.gisDataForm.get('streetNoAddition').patchValue('');
    this.gisDataForm.get('uniqueCoId').patchValue('');
  }

  emitGisData() {
    this.gisData.emit({
      gisData: this.selectedGisData,
      objectNotFound: this.isNotFound,
      areaAvailable: this.areaAvailable
    });
  }

  setNullCompanyLocation() {
    this.resetCompanyLocation.emit(true);
  }

  saveData() {
    this.gisDataForm.get('streetNo').clearValidators();
    this.gisDataForm.get('streetNo').markAsTouched();
    this.isCompleted = true;
  }

  confirmCorridorNumber() {
    this.saveUniqueCoId();
    this.gisDataForm.get('uniqueCoId').patchValue(this.selectedGisData.uniqueCoId);
    this.areaAvailable = true;
    this.isCompleted = true;
  }

  get zipCodeCityField() {
    return this.gisDataForm.get('zipCodeCity');
  }

  get streetField() {
    return this.gisDataForm.get('street');
  }

  get streetNoField() {
    return this.gisDataForm.get('streetNo');
  }

  get streetNoAdditionField() {
    return this.gisDataForm.get('streetNoAddition');
  }

  get newDevelopmentAreaField() {
    return this.gisDataForm.get('developmentAreaName');
  }

  get corridorNumberField() {
    return this.gisDataForm.get('corridorNumber');
  }

  resetChangeValues() {
    this.compUnsubs$.next();
    setTimeout(() => {
      this.searchCityZipCode();
      this.searchNewDevelopmentArea();
      this.searchStreet();
      this.searchStreetNo();
      this.observeCorridorNumberChanges();
    }, 400);
  }

  saveUniqueCoId() {
    this.selectedGisData.uniqueCoId =
      'ZIP' +
      this.selectedGisData.cityId +
      'STREET' +
      this.selectedGisData.streetId +
      'NUMBER' +
      this.selectedGisData.houseNo +
      'NUMBERADD' +
      (this.selectedGisData.houseNoAddition ? this.selectedGisData.houseNoAddition : '');
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}

export type GisDataEvent = {
  areaAvailable: boolean;
  gisData: GisDataModel;
  objectNotFound: boolean;
};
